import type { ConnectError } from '@connectrpc/connect'
import {
  TradingPartnerErrorReason,
  TradingPartnerErrorReasonSchema,
} from '@ebalance/proto/bffuser/v1/occto_master_model_pb'
import type { TFunction } from 'i18next'

import {
  type AppErrorInfoReasonEntry,
  type AppErrorInfoResolution,
  resolveAppErrorInfoMessage,
} from '~/libs/appErrorInfo'

/**
 * `TradingPartnerErrorReason` (backend `occtomaster` domain) → i18n key
 * registry for the TradingPartner Create/Update modals (mirrors BgMaster's
 * pattern).
 *
 * All mapped reasons are operation-scoped:
 * - `RepresentativeCodeAlreadyExists` (spec AC-OMT-028): cross-partner
 *   representative-code uniqueness. Its message template interpolates
 *   `{{code}}` / `{{partnerName}}`, which `resolveAppErrorInfoMessage` fills
 *   automatically from the ErrorInfo detail's `metadata`.
 * - `AccountCodeAlreadyExistsCrossMaster` (AC-MOI-013): account code
 *   collides with an existing BG master code (Create / Update).
 * - `AccountCodeReferencedByPlanMaster` (spec AC-T08): Update would remove
 *   an account code still referenced by a plan master.
 */
const TRADING_PARTNER_REASON_KEYS: Record<
  TradingPartnerErrorReason,
  AppErrorInfoReasonEntry | undefined
> = {
  [TradingPartnerErrorReason.UNSPECIFIED]: undefined,
  [TradingPartnerErrorReason.REPRESENTATIVE_CODE_ALREADY_EXISTS]: {
    key: 'page:adminOcctoMaster.tradingPartner.serverError.reason.representativeCodeAlreadyExists',
  },
  [TradingPartnerErrorReason.ACCOUNT_CODE_ALREADY_EXISTS_CROSS_MASTER]: {
    key: 'page:adminOcctoMaster.tradingPartner.serverError.reason.accountCodeAlreadyExistsCrossMaster',
  },
  [TradingPartnerErrorReason.ACCOUNT_CODE_REFERENCED_BY_PLAN_MASTER]: {
    key: 'page:adminOcctoMaster.tradingPartner.serverError.reason.accountCodeReferencedByPlanMaster',
  },
}

/**
 * `resolveDetailedError` for TradingPartner's `useCrudModalForm` (Create /
 * Update). Resolves the `google.rpc.ErrorInfo` Connect error detail, if
 * any, against `TRADING_PARTNER_REASON_KEYS` via the
 * `TradingPartnerErrorReasonSchema` reflection lookup.
 */
export function resolveTradingPartnerDetailedError(
  err: ConnectError,
  t: TFunction,
): AppErrorInfoResolution | undefined {
  return resolveAppErrorInfoMessage(
    err,
    TradingPartnerErrorReasonSchema,
    TRADING_PARTNER_REASON_KEYS,
    t,
  )
}
